// eslint-disable-next-line no-restricted-imports -- Use the SDK contract for boundary types.
import { type CaptureResult } from 'posthog-js';

import { isDefined, isDefinedObject, isTruthy } from 'common';

import { normalizeTelemetryRoute } from './normalizeRoute';

function sanitizeUrl(value: string) {
  try {
    const url = new URL(value, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return undefined;
    }
    url.username = '';
    url.password = '';
    url.pathname = normalizeTelemetryRoute(url.pathname);
    url.search = '';
    url.hash = url.hash.startsWith('#/') ? `#${normalizeTelemetryRoute(url.hash.slice(1))}` : '';
    return value.startsWith('/') && !value.startsWith('//')
      ? `${url.pathname}${url.hash}`
      : url.toString();
  } catch {
    return undefined;
  }
}

/** Links to other hosts keep their path for analytics; staff links lose record identifiers like url properties do. */
function sanitizeLink(value: string) {
  const trimmedValue = value.trim();
  if (
    (trimmedValue.startsWith('/') && !trimmedValue.startsWith('//')) ||
    /^[?#\\]|^\.{1,2}[/\\]/.test(trimmedValue)
  ) {
    return sanitizeUrl(trimmedValue);
  }
  if (/^(?:data|javascript):/i.test(trimmedValue)) {
    return undefined;
  }
  if (!trimmedValue.startsWith('//') && !/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmedValue)) {
    return value;
  }
  try {
    const url = new URL(value, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return undefined;
    }
    if (url.origin === window.location.origin) {
      return sanitizeUrl(value);
    }
    url.username = '';
    url.password = '';
    url.search = '';
    url.hash = url.hash.startsWith('#/') ? `#${normalizeTelemetryRoute(url.hash.slice(1))}` : '';
    return url.toString();
  } catch {
    return undefined;
  }
}

function sanitizeString(options: { value: string; normalizedKey: string }) {
  const { value, normalizedKey } = options;
  if (/(?:^\$?|_)pathnames?$/.test(normalizedKey)) {
    return normalizeTelemetryRoute(value);
  }
  if (/(?:^\$?|_)(?:urls?|referrers?)$/.test(normalizedKey)) {
    return value === '$direct' ? value : sanitizeUrl(value);
  }
  return sanitizeLink(value);
}

function sanitizeValue(options: { value: unknown; key: string }): unknown {
  const { value, key } = options;
  if (Array.isArray(value)) {
    return value.flatMap((item: unknown) => {
      const sanitized = sanitizeValue({ value: item, key });
      return isDefined(item) && !isDefined(sanitized) ? [] : [sanitized];
    });
  }
  const normalizedKey = key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
  if (/(?:^\$?|_)(?:host|referring_domain)$/.test(normalizedKey)) {
    return value === window.location.host ||
      (normalizedKey.endsWith('referring_domain') && value === '$direct')
      ? value
      : undefined;
  }
  if (typeof value === 'string') {
    return sanitizeString({ value, normalizedKey });
  }
  if (isDefinedObject(value) && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.entries(value).flatMap(([propertyKey, propertyValue]) => {
        const sanitized = sanitizeValue({ value: propertyValue, key: propertyKey });
        return isDefined(propertyValue) && !isDefined(sanitized) ? [] : [[propertyKey, sanitized]];
      })
    );
  }
  return value;
}

/** Urls may carry patient identifiers in queries and paths, and the SDK copies them into event and person properties. */
export function sanitizeUrls(event: CaptureResult): CaptureResult | null {
  const properties = sanitizeValue({ value: event.properties, key: '' });
  if (!isDefinedObject(properties)) {
    return null;
  }
  const personProperties = sanitizeValue({ value: event.$set, key: '' });
  const initialPersonProperties = sanitizeValue({ value: event.$set_once, key: '' });
  /** The app uses a hash router, so the route lives in the hash rather than location.pathname. */
  const routePath = window.location.hash.replace(/^#/, '').split('?')[0];
  return {
    ...event,
    ...(isDefinedObject(personProperties) ? { $set: personProperties } : {}),
    ...(isDefinedObject(initialPersonProperties) ? { $set_once: initialPersonProperties } : {}),
    properties: {
      ...properties,
      ...(isTruthy(routePath.length) ? { $pathname: normalizeTelemetryRoute(routePath) } : {}),
    },
  };
}
